import { createApp } from 'vue';
import App from '@CCS/App.vue';
import router from '@CCS/router';
import { createPinia } from 'pinia';
import Toast from 'vue-toastification';
// Import the CSS or use your own!
import 'vue-toastification/dist/index.css';
import 'uno.css';
import piniaPluginPersistedstate from 'pinia-plugin-persistedstate';

import { registerComponents } from '@Shared/components/visuals/register';
import Dashboard from '@CCS/components/Dashboard.vue';
import { useRootStore } from '@Shared/stores/root';
import { defineNewAppInsights } from '@Shared/utils/util';

const pinia = createPinia();
pinia.use(({ store }) => {
  store.router = markRaw(router);
});
pinia.use(piniaPluginPersistedstate);

(async () => {
  const appInsights = await defineNewAppInsights();

  // Track global errors
  window.addEventListener('error', (event) => {
    appInsights.trackException({
      exception: event.error || event.message,
      properties: {
        source: 'window.onerror',
        info: `${event.filename}:${event.lineno}:${event.colno}`,
      },
    });
    appInsights.flush();
  });

  // Track unhandled promise rejections
  window.addEventListener('unhandledrejection', (event) => {
    appInsights.trackException({
      exception: event.reason,
      properties: {
        source: 'window.onunhandledrejection',
      },
    });
    appInsights.flush();
  });

  const app = createApp(App)
    .use(pinia)
    .use(router)
    .use(() => {
      appInsights.trackPageView();
      appInsights.addTelemetryInitializer((item) => {
        const root = useRootStore();
        if (item.data) {
          item.data.clientId = root.clientId;
          item.data.environment = root.environment;
          item.data.frontendVersion = root.frontendVersion;
          item.data.product = 'CustomerCentreStage';
          item.data.businessUnit = root.token?.scope;
        }
      });

      router.beforeEach((route, _, next) => {
        appInsights.startTrackPage(route.name?.toString());
        next();
      });
      router.afterEach((route) => {
        appInsights.stopTrackPage(route.name?.toString());
        appInsights.flush();
      });
      appInsights.startTrackPage(router.currentRoute.value.name?.toString());
    })
    .use(Toast, {
      maxToasts: 5,
      newestOnTop: true,
      filterBeforeCreate: (toast: any, toasts: any) => {
        // FIXME: replace with a smarter method later for now we are just going to check equivalence badly
        toast.pauseOnFocusLoss = false;
        if (!toast.typeOfContent) return toast;

        if (
          toasts.filter((t: any) => t.type == toast.type && t.typeOfContent && t.typeOfContent == toast.typeOfContent)
            .length > 0
        ) {
          return false;
        }

        return toast;
      },
    });

  app.config.errorHandler = (err, vm, info) => {
    appInsights.trackException({
      exception: err as Error,
      properties: {
        component: vm?.$options?.name || 'UnknownComponent',
        info,
      },
    });
    appInsights.flush();
  };

  registerComponents((componentName, component) => app.component(componentName, component));
  app.component('Dashboard', Dashboard);

  app.mount('#app');
})();
